import { get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import { eq } from 'ember-truth-helpers';
import HotelRatesTablesCellsTdBase from 'lumen/components/hotel/rates/tables/cells/td-base';
import changeColorClass from 'lumen/helpers/change-color-class';
import {
  COMPARETYPES,
  MARKET_DEMAND_LEVELS,
  MARKET_DEMAND_LEVELS_CLASSES,
} from 'lumen/utils/constants/general-constants';
import { stringifyObject } from 'lumen/utils/parse-utils';

const SIGNIFICANT_DEMAND_CHANGE = 0.03;

export default class HotelRatesTablesCellsTdMarketDemand extends HotelRatesTablesCellsTdBase {
  @service declare access: Services['access'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  MARKET_DEMAND_LEVELS_CLASSES = MARKET_DEMAND_LEVELS_CLASSES;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demand() {
    return this.day.demand;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demandChange() {
    const referenceValue = this.demand.demandChanges.find(
      (demandChange) => demandChange.extract_date === this.compareValue,
    )?.value;

    if (referenceValue) {
      const demandChange = this.demand.value - referenceValue;
      if (Math.abs(demandChange) > SIGNIFICANT_DEMAND_CHANGE) {
        return demandChange;
      }
    }

    return 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demandLevels() {
    return Object.values(MARKET_DEMAND_LEVELS);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDemandChange() {
    if (this.demandChange) {
      return this.intl.formatNumber(this.demandChange, {
        style: 'percent',
        signDisplay: 'always',
      });
    }

    return '';
  }

  get layoutConditionalClassNames(): string {
    const { layout, textAlign } = this.args.column;
    const alignment = textAlign === 'left' ? 'items-start' : 'items-end';
    const justify = textAlign === 'left' ? 'justify-start' : 'justify-end';
    const isRowLayout = layout === 'side-by-side';

    return isRowLayout
      ? `td-market-demand--row ${justify}`
      : `td-market-demand--column ${alignment}`;
  }

  get showDemandChanges(): boolean {
    return (
      this.compareType === COMPARETYPES.DATE &&
      this.compareValue !== undefined &&
      this.demand !== null &&
      !this.demand.isMissing &&
      this.demand.demandChanges !== undefined &&
      Boolean(this.demandChange)
    );
  }

  @action
  redirectToMarketInsight(): void {
    if (!this.access.hasMarketInsight) {
      return;
    }

    this.usageAnalytics.track('Market Demand Explored', {
      eventTrigger: 'market-demand-table-view',
      marketDemandValueClicked: this.demand.formattedValue,
    });

    this.router.transitionTo('hotel.market-insight', {
      queryParams: {
        activeFilters: stringifyObject({ day: this.day.formattedDate }),
      },
    });
  }

  <template>
    {{#unless this.demand.isOutOfRange}}
      <div
        class="{{this.layoutConditionalClassNames}}
          text-200--regular tabular-nums
          {{if this.isPast 'td-past'}}
          {{if this.isToday 'td-today'}}"
      >
        <div
          class="flex
            {{if (eq @column.layout 'side-by-side') 'flex-row-reverse'}}
            gap-100"
        >
          <p
            class={{if this.access.hasMarketInsight "link"}}
            role="button"
            {{on "click" this.redirectToMarketInsight}}
          >
            {{this.demand.formattedValue}}
          </p>

          <div class="flex h-400 w-400 justify-center">
            <div
              class="identifier-square {{this.demand.demandLevelClass}}"
            ></div>
          </div>
        </div>
        <div>
          {{#if this.showDemandChanges}}
            <p
              class="mr-500 {{changeColorClass this.demandChange}}"
            >{{this.formattedDemandChange}}</p>
          {{else if this.compareType}}
            &nbsp;
          {{/if}}
        </div>
        {{#unless this.demand.isMissing}}
          <EmberTooltip @side="left" @tooltipClass="default-tooltip">
            <div class="flex flex-col gap-300 p-200">
              <p
                class="text-300--regular text-neutral-default"
              >{{this.demand.formattedDemandLevel}}</p>
              <div class="flex items-center justify-start gap-100">
                {{#each this.demandLevels as |level|}}
                  <div
                    class="identifier-square
                      {{get this.MARKET_DEMAND_LEVELS_CLASSES level}}
                      {{if
                        (eq this.demand.demandLevel level)
                        'identifier-square--selected'
                      }}"
                  ></div>
                {{/each}}
              </div>
            </div>
          </EmberTooltip>
        {{/unless}}
      </div>
    {{/unless}}
  </template>
}
